import QRCode from 'qrcode';
import type { ShareLink, ShareLinkType } from '../../services/api';
import { saveBlobDownload } from '../../../shared/utils/saveBlobDownload';

export const SHARE_LINK_TYPE_LABELS: Record<ShareLinkType, string> = {
  MAP_3D: '3D Map',
  SUPER_RATIO: 'Super Ratio',
  PRICE_PROMO: 'Price Promo',
  THUMBSUP_UNIT: 'Thumbs-up Unit',
};

/** The link a consumer should receive: the short one when the shortener worked. */
export const shareLinkUrl = (link: Pick<ShareLink, 'shortUrl' | 'fullUrl'>): string => link.shortUrl || link.fullUrl;

type MessageLink = Pick<ShareLink, 'shortUrl' | 'fullUrl' | 'projectName' | 'includeSunlight'>;

/** "Here's an interactive 3D map of … :" — the line every share message opens with, after the greeting. */
function map3dPitch(link: MessageLink): string {
  const project = link.projectName || 'this project';
  if (project.trim().replace(/\s+/g, ' ').toLowerCase() === 'thomson reserve') {
    if (link.includeSunlight) {
      return `Here's an interactive 3D map of ${project} — orbit the development, explore every stack, browse curated views from key points around the site and see how the sun moves across the blocks through the day:`;
    }
    return `Here's an interactive 3D map of ${project} — orbit the development, explore every stack and browse curated views from key points around the site:`;
  }
  return `Here's an interactive 3D map of ${project} — spin the site, explore every stack and see how the sun moves across the blocks through the day:`;
}

export function shareLinkMessage(link: MessageLink): string {
  return `Hi! ${map3dPitch(link)}\n${shareLinkUrl(link)}`;
}

/** Starting messages in the "Send to my clients" sheet; placeholders are filled per client. */
export function clientMessageTemplates(link: MessageLink): Array<{ key: string; label: string; body: string }> {
  const pitch = map3dPitch(link);
  return [
    { key: 'quick', label: 'Quick intro', body: `Hi {{client_name}}! ${pitch}\n{{link}}` },
    {
      key: 'personal',
      label: 'Personal',
      body: `Hi {{client_name}}, this is {{agent_name}}. ${pitch}\n{{link}}\n\nHappy to walk you through it or arrange a viewing — just let me know!`,
    },
  ];
}

export const CLIENT_MESSAGE_PLACEHOLDERS = ['{{client_name}}', '{{agent_name}}', '{{link}}'] as const;

/** What to call a client in the greeting: their name, or "there" when the name is only a number. */
export const greetingName = (name: string): string => (/[^\d\s+\-().]/.test(name) ? name.trim() : 'there');

export function fillClientMessage(
  template: string,
  values: { clientName: string; agentName: string; link: string },
): string {
  return template
    .replace(/\{\{\s*client_name\s*\}\}/gi, greetingName(values.clientName))
    .replace(/\{\{\s*agent_name\s*\}\}/gi, values.agentName)
    .replace(/\{\{\s*link\s*\}\}/gi, values.link);
}

export const whatsAppShareUrl = (link: MessageLink): string =>
  `https://wa.me/?text=${encodeURIComponent(shareLinkMessage(link))}`;

/** A wa.me chat with one number (digits, country code included), message pre-filled. */
export const whatsAppChatUrl = (waNumber: string, message: string): string =>
  `https://wa.me/${waNumber}?text=${encodeURIComponent(message)}`;

export function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const minutes = Math.round((Date.now() - then) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-SG', {
    timeZone: 'Asia/Singapore', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
  });
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Older WebViews: fall back to a hidden textarea.
    const area = document.createElement('textarea');
    area.value = text;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    area.remove();
    return ok;
  }
}

export async function downloadShareLinkQr(link: ShareLink): Promise<void> {
  const dataUrl = await QRCode.toDataURL(shareLinkUrl(link), { width: 1024, margin: 2, errorCorrectionLevel: 'M' });
  const blob = await (await fetch(dataUrl)).blob();
  const slug = (link.projectName || 'project').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const date = new Date().toISOString().slice(0, 10);
  await saveBlobDownload(blob, `${slug}-3d-map-qr-${date}.png`, 'image/png');
}
